import { createContext, useContext, useEffect, useState, useCallback, type ReactNode } from 'react';
import { getAccessToken } from '../lib/auth-api';
import { useAuth } from './AuthContext';

interface Entreprise {
  id: string;
  raison_sociale: string;
  forme_juridique?: string;
  exercices?: { id: string; debut: string; fin: string; is_current: boolean }[];
  [key: string]: any;
}

interface CompanyState {
  entrepriseId: string | null;
  exerciceId: string | null;
  entreprises: Entreprise[];
  companiesLoaded: boolean;
  setActiveCompany: (id: string, exerciceId: string) => void;
  refreshCompanies: () => Promise<void>;
}

const CompanyContext = createContext<CompanyState | undefined>(undefined);

export function CompanyProvider({ children }: { children: ReactNode }) {
  const { session } = useAuth();
  const [entrepriseId, setEntrepriseId] = useState<string | null>(
    () => localStorage.getItem('entreprise_id')
  );
  const [exerciceId, setExerciceId] = useState<string | null>(
    () => localStorage.getItem('exercice_id')
  );
  const [entreprises, setEntreprises] = useState<Entreprise[]>([]);
  const [companiesLoaded, setCompaniesLoaded] = useState(false);

  const fetchCompanies = useCallback(async () => {
    try {
      const token = getAccessToken();
      if (!token) {
        // No session — don't mark as loaded, keep previous state
        return;
      }

      const res = await fetch(`${import.meta.env.VITE_API_URL || ''}/api/entreprises`, {
        headers: { Authorization: `Bearer ${token}` },
        credentials: 'include',
      });
      if (res.ok) {
        const data: Entreprise[] = await res.json();
        setEntreprises(data);

        const storedEid = localStorage.getItem('entreprise_id');
        const storedMatch = storedEid ? data.find((e) => e.id === storedEid) : undefined;

        if (storedEid && !storedMatch) {
          // Stored entrepriseId no longer valid — clear it
          localStorage.removeItem('entreprise_id');
          localStorage.removeItem('exercice_id');
          setEntrepriseId(null);
          setExerciceId(null);
        }

        // Auto-select first entreprise on new device (no valid stored selection yet)
        if (!storedMatch && data.length > 0) {
          const first = data[0];
          const currentExercice = first.exercices?.find((ex) => ex.is_current) ?? first.exercices?.[0];
          if (currentExercice) {
            setEntrepriseId(first.id);
            setExerciceId(currentExercice.id);
            localStorage.setItem('entreprise_id', first.id);
            localStorage.setItem('exercice_id', currentExercice.id);
          }
        }
      }
      // Mark loaded after successful session+fetch
      setCompaniesLoaded(true);
    } catch {
      // silently fail — companies list is non-critical, but mark loaded to avoid modal while debugging
      setCompaniesLoaded(true);
    }
  }, []);

  // Re-fetch whenever a session is established (covers OAuth callback and normal login)
  useEffect(() => {
    if (session) {
      fetchCompanies();
    }
  }, [session, fetchCompanies]);

  const setActiveCompany = (id: string, exId: string) => {
    setEntrepriseId(id);
    setExerciceId(exId);
    localStorage.setItem('entreprise_id', id);
    localStorage.setItem('exercice_id', exId);
  };

  return (
    <CompanyContext.Provider
      value={{ entrepriseId, exerciceId, entreprises, companiesLoaded, setActiveCompany, refreshCompanies: fetchCompanies }}
    >
      {children}
    </CompanyContext.Provider>
  );
}

export function useCompany(): CompanyState {
  const ctx = useContext(CompanyContext);
  if (!ctx) throw new Error('useCompany must be used within CompanyProvider');
  return ctx;
}
